import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ─────────────────────────────────────────────────────────────────────────────
// C14-E.6 httpOnly cookies migration (frontend counterpart of T2-C backend).
// ─────────────────────────────────────────────────────────────────────────────
// The refresh token is now emitted by the admin-api as an HttpOnly + Secure +
// SameSite=Lax cookie scoped to /api/v1/auth. Storing a copy in the browser
// (Zustand persist → localStorage) was the main XSS-exposure vector, so it is
// intentionally removed here:
//
//   - The store no longer carries `refreshToken`.
//   - `setTokens(a, r)` → `setAccessToken(a)` (single argument, matches the
//     /refresh response shape `{ accessToken }`).
//   - `persist.partialize` includes only `accessToken` + `staff` (both already
//     considered low-risk — accessToken is short-lived and rotated by the
//     refresh interceptor, staff is non-sensitive profile data). `refreshToken`
//     is omitted explicitly; `isAuthenticated` is derived at rehydrate time so
//     the hydrated state cannot go out of sync with the server cookie.
//
// apps/web / apps/api were NOT migrated this cycle (backend still accepts body
// refreshToken). Pairing is tracked in CYCLE-13-14 backlog.
// ─────────────────────────────────────────────────────────────────────────────

interface Staff {
  id: string;
  email: string;
  nombre: string;
  role: string;
}

interface AuthState {
  accessToken: string | null;
  staff: Staff | null;
  isAuthenticated: boolean;

  // Actions
  setAccessToken: (accessToken: string) => void;
  setStaff: (staff: Staff) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      staff: null,
      isAuthenticated: false,

      setAccessToken: (accessToken) =>
        set({
          accessToken,
          isAuthenticated: true,
        }),

      setStaff: (staff) =>
        set({
          staff,
        }),

      logout: () =>
        set({
          accessToken: null,
          staff: null,
          isAuthenticated: false,
        }),
    }),
    {
      name: 'solidcycle-admin-auth',
      // C14-E.6: refreshToken deliberately excluded — lives in HttpOnly cookie.
      partialize: (state) => ({
        accessToken: state.accessToken,
        staff: state.staff,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
